/**
 * BDSJEA-166 - Panel de procesos programados.
 *
 * Se monta como solapa dentro de Administración. No tiene ruta propia ni objeto
 * de menú: hereda el permiso de /agro/admin. El backend igual valida
 * ADMINISTRADOR en cada endpoint — una solapa oculta no es un control de acceso.
 */
import React, { useCallback, useEffect, useState } from "react"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Table,
} from "reactstrap"
import dayjs from "dayjs"

import { getProcesos, getHistorialProceso, ejecutarProceso } from "api/agro"
import toastHandler from "toastHandler"

const COLOR_RESULTADO = {
  OK: "success",
  ERROR: "danger",
  EN_CURSO: "info",
}

const COLOR_CARGA = {
  LIVIANO: "light",
  MEDIO: "warning",
  PESADO: "danger",
}

const formatearDuracion = ms => {
  if (ms == null) return "—"
  if (ms < 1000) return `${ms} ms`
  const seg = ms / 1000
  if (seg < 60) return `${seg.toFixed(1)} s`
  const min = Math.floor(seg / 60)
  return `${min} min ${Math.round(seg % 60)} s`
}

const formatearFecha = f => (f ? dayjs(f).format("DD/MM/YYYY HH:mm") : "—")

const ProcesosPanel = () => {
  const setSuccess = toastHandler(s => s.setSuccess)
  const setError = toastHandler(s => s.setError)

  const [procesos, setProcesos] = useState([])
  const [cargando, setCargando] = useState(true)
  const [ejecutando, setEjecutando] = useState(null)
  const [historial, setHistorial] = useState(null)
  const [procesoHistorial, setProcesoHistorial] = useState(null)

  const cargar = useCallback(
    async (silencioso = false) => {
      if (!silencioso) setCargando(true)
      try {
        setProcesos(await getProcesos())
      } catch (error) {
        setError("No se pudieron cargar los procesos: " + error.message)
      } finally {
        setCargando(false)
      }
    },
    [setError],
  )

  useEffect(() => {
    cargar()
  }, [cargar])

  // Refresco silencioso mientras haya algo corriendo. Se apaga solo: sin
  // procesos en curso no tiene sentido consultar cada diez segundos.
  useEffect(() => {
    const hayEnCurso = procesos.some(p => p.enCurso)
    if (!hayEnCurso) return undefined
    const id = setInterval(() => cargar(true), 10000)
    return () => clearInterval(id)
  }, [procesos, cargar])

  const handleEjecutar = async proceso => {
    if (
      proceso.carga === "PESADO" &&
      !window.confirm(
        `"${proceso.nombre}" es un proceso pesado y puede tardar varios minutos, ` +
          `además de generar carga sobre el servidor y las fuentes externas.\n\n¿Ejecutar igual?`,
      )
    ) {
      return
    }
    setEjecutando(proceso.codigo)
    try {
      const r = await ejecutarProceso(proceso.codigo)
      setSuccess(r.mensaje || "Proceso encolado")
      await cargar(true)
    } catch (error) {
      const detalle =
        error?.response?.data?.error ||
        error?.response?.data?.mensaje ||
        error.message
      setError(detalle)
    } finally {
      setEjecutando(null)
    }
  }

  const verHistorial = async proceso => {
    setProcesoHistorial(proceso)
    setHistorial(null)
    try {
      setHistorial(await getHistorialProceso(proceso.codigo, 20))
    } catch (error) {
      setError("No se pudo cargar el historial: " + error.message)
      setHistorial([])
    }
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">
          <i className="mdi mdi-cog-sync me-2 text-primary" />
          Procesos programados
        </h5>
        <Button color="link" size="sm" onClick={() => cargar()}>
          <i className="mdi mdi-refresh me-1" />
          Refrescar
        </Button>
      </div>

      <p className="text-muted small">
        Procesos automáticos del módulo agropecuario. La programación se define
        en la configuración del despliegue y no se edita desde acá; lo que sí se
        puede es forzar una corrida en el momento.
      </p>

      {cargando ? (
        <div className="text-center py-4">
          <i className="mdi mdi-loading mdi-spin" /> Cargando procesos...
        </div>
      ) : (
        <Row>
          {procesos.map(p => (
            <Col md={6} key={p.codigo} className="mb-3">
              <Card className="h-100">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 className="mb-1">
                        {p.nombre}
                        {!p.habilitado && (
                          <Badge color="secondary" className="ms-2">
                            Deshabilitado
                          </Badge>
                        )}
                        {p.enCurso && (
                          <Badge color="info" className="ms-2">
                            <i className="mdi mdi-loading mdi-spin me-1" />
                            En curso
                          </Badge>
                        )}
                      </h6>
                      <div className="text-muted small mb-2">{p.categoria}</div>
                    </div>
                    <Badge color={COLOR_CARGA[p.carga] || "light"} pill>
                      {p.carga}
                    </Badge>
                  </div>

                  <p className="small mb-2">{p.descripcion}</p>

                  <div className="small text-muted mb-2">
                    <div>
                      <i className="mdi mdi-clock-outline me-1" />
                      {p.cronDescripto || p.cron}
                      {p.cronDescripto && (
                        <code className="ms-2 small">{p.cron}</code>
                      )}
                    </div>
                    {p.proximaEjecucion && (
                      <div>
                        <i className="mdi mdi-calendar-clock me-1" />
                        Próxima: {formatearFecha(p.proximaEjecucion)}
                      </div>
                    )}
                  </div>

                  <div className="border-top pt-2 mb-2 small">
                    {p.ultimaFecha ? (
                      <>
                        <Badge
                          color={
                            COLOR_RESULTADO[p.ultimoResultado] || "secondary"
                          }
                        >
                          {p.ultimoResultado}
                        </Badge>{" "}
                        {formatearFecha(p.ultimaFecha)} ·{" "}
                        {formatearDuracion(p.ultimaDuracionMs)}
                        {p.ultimoDisparo === "MANUAL" && (
                          <span className="text-muted">
                            {" "}
                            · manual ({p.ultimoUsuario})
                          </span>
                        )}
                        {p.ultimoResultado === "ERROR" && p.ultimoMensaje && (
                          <div className="text-danger mt-1">
                            {p.ultimoMensaje}
                          </div>
                        )}
                      </>
                    ) : (
                      <span className="text-muted">
                        Sin ejecuciones registradas
                      </span>
                    )}
                  </div>

                  {/* Qué se rompe si el proceso deja de correr. Es la
                      información que hace falta para decidir si vale la pena
                      forzar una corrida. */}
                  <details className="small text-muted mb-2">
                    <summary style={{ cursor: "pointer" }}>
                      Si no corre...
                    </summary>
                    <div className="mt-1">{p.siNoCorre}</div>
                  </details>

                  <div className="d-flex gap-2">
                    <Button
                      color="primary"
                      size="sm"
                      outline
                      disabled={p.enCurso || ejecutando === p.codigo}
                      onClick={() => handleEjecutar(p)}
                    >
                      <i className="mdi mdi-play me-1" />
                      {ejecutando === p.codigo
                        ? "Encolando..."
                        : "Ejecutar ahora"}
                    </Button>
                    <Button
                      color="secondary"
                      size="sm"
                      outline
                      onClick={() => verHistorial(p)}
                    >
                      <i className="mdi mdi-history me-1" />
                      Historial
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Modal
        isOpen={!!procesoHistorial}
        toggle={() => setProcesoHistorial(null)}
        size="lg"
      >
        <ModalHeader toggle={() => setProcesoHistorial(null)}>
          Historial — {procesoHistorial?.nombre}
        </ModalHeader>
        <ModalBody>
          {historial === null ? (
            <div className="text-center py-3">
              <i className="mdi mdi-loading mdi-spin" /> Cargando...
            </div>
          ) : historial.length === 0 ? (
            <p className="text-muted mb-0">Sin ejecuciones registradas.</p>
          ) : (
            <Table size="sm" responsive className="mb-0">
              <thead>
                <tr>
                  <th>Inicio</th>
                  <th>Disparo</th>
                  <th>Duración</th>
                  <th>Resultado</th>
                  <th>Mensaje</th>
                </tr>
              </thead>
              <tbody>
                {historial.map(e => (
                  <tr key={e.id}>
                    <td className="small">{formatearFecha(e.fechaInicio)}</td>
                    <td className="small">
                      {e.disparo}
                      {e.usuario && (
                        <div className="text-muted">{e.usuario}</div>
                      )}
                    </td>
                    <td className="small">{formatearDuracion(e.duracionMs)}</td>
                    <td>
                      <Badge
                        color={COLOR_RESULTADO[e.resultado] || "secondary"}
                      >
                        {e.resultado}
                      </Badge>
                    </td>
                    <td className="small">{e.mensaje}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </ModalBody>
      </Modal>
    </>
  )
}

export default ProcesosPanel
